import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Award,
  BarChart3,
  Users,
  CheckCircle,
  ArrowRight,
  Calendar,
  Send,
} from "lucide-react";
import { useState } from "react";

import { Button } from "./ui/button";
import { HeroInteractive } from "./main/HeroInteractive";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { useToast } from "./ui/toast";
import { createIntroRequest } from "@/lib/datasource/supabase";
import { useAuth } from "@/auth/SupabaseAuthProvider";
import { modalActions } from "@/stores/modal-store"; 

function TrustBadge({ icon: Icon, text }: { icon: any; text: string }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm">
      <Icon className="h-4 w-4 text-primary" />
      <span className="text-foreground/80 font-medium">{text}</span>
    </div>
  );
}

export default function HeroInvestmentPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [introDialogOpen, setIntroDialogOpen] = useState(false);
  const [introForm, setIntroForm] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!introForm.email) {
      toast.error("Email is required");
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await createIntroRequest({
        name: introForm.name || "Test Intro",
        email: introForm.email,
        message:
          introForm.message || "Testing intro through consulting channel",
      });

      if (result.error) {
        throw new Error(result.error);
      }

      toast.success("Request sent! We'll respond within 24 hours.");
      setIntroDialogOpen(false);
      setIntroForm({ name: "", email: "", message: "" });

    } catch (error) {
      console.error("Failed to submit intro request:", error);
      toast.error("Failed to send request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartAnalysis = () => {
    if (!user) {
      modalActions.open("signin");
    } else {
      navigate("/dashboard");
    }
  };

  return (
    <section className="relative pt-16 pb-20 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/5" />
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-accent/10 to-transparent" />

      <div className="container mx-auto px-4 relative">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8 px-6 lg:px-0">
            <motion.h1
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
              className="text-5xl md:text-6xl font-bold text-foreground leading-tight"
            >
              Build hotels.{" "}
              <span className="text-primary-blue">Not spreadsheets.</span>
            </motion.h1>
            <p className="text-xl text-muted-foreground max-w-xl">
              The operating system for boutique hotel investors. From napkin
              sketch to opening night — we have got your back.
            </p>

            <div className="flex flex-wrap gap-4">
              <Button
                size="lg"
                onClick={handleStartAnalysis}
                className="gap-2 bg-[hsl(215,85%,45%)] hover:bg-[hsl(215,85%,45%)]/90 text-white"
              >
                Analyze your first deal
                <ArrowRight className="h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => setIntroDialogOpen(true)}
                className="gap-2"
              >
                <Calendar className="h-5 w-5" />
                Schedule a free intro
              </Button>
            </div>

            <div className="flex flex-wrap gap-3 pt-4">
              <TrustBadge icon={Award} text="USALI-compliant" />
              <TrustBadge icon={BarChart3} text="50+ hotel benchmarks" />
            </div>
            <div>
              <TrustBadge icon={Users} text="20+ expert consultants on tap" />
            </div>

            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-primary-blue" />
                Model any deal in under 10 minutes
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-primary-blue" />
                Real market salary & cost data
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-primary-blue" />
                Built by hoteliers, for hoteliers
              </li>
            </ul>
          </div>

          <div className="lg:pl-8">
            <HeroInteractive />
          </div>

          <Dialog open={introDialogOpen} onOpenChange={setIntroDialogOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader className="px-6 pt-6 pb-4">
                <DialogTitle className="text-xl font-semibold">
                  Schedule a Free Intro
                </DialogTitle>
                <DialogDescription className="text-sm text-muted-foreground mt-2">
                  Tell us about your hotel project and we'll set up a
                  personalized walkthrough.
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-5">
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="intro-name" className="text-sm font-medium">
                      Your name
                    </Label>
                    <Input
                      id="intro-name"
                      value={introForm.name}
                      onChange={(e) =>
                        setIntroForm({ ...introForm, name: e.target.value })
                      }
                      placeholder="John Smith"
                      className="h-10"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="intro-email"
                      className="text-sm font-medium"
                    >
                      Email *
                    </Label>
                    <Input
                      id="intro-email"
                      type="email"
                      value={introForm.email}
                      onChange={(e) =>
                        setIntroForm({ ...introForm, email: e.target.value })
                      }
                      placeholder="john@example.com"
                      required
                      className="h-10"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="intro-message"
                      className="text-sm font-medium"
                    >
                      Tell us about your project (optional)
                    </Label>
                    <Textarea
                      id="intro-message"
                      value={introForm.message}
                      onChange={(e) =>
                        setIntroForm({ ...introForm, message: e.target.value })
                      }
                      placeholder="I'm looking to acquire a 20-room boutique hotel in Portugal..."
                      rows={4}
                      className="min-h-[100px] resize-none"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting || !introForm.email}
                  className="w-full h-11 gap-2 mt-2 bg-[hsl(215,85%,45%)] hover:bg-[hsl(215,85%,45%)]/90 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Send Request
                    </>
                  )}
                </Button>

                <p className="text-xs text-muted-foreground text-center pt-2">
                  We'll respond within 24 hours to schedule your free 30-minute
                  intro call.
                </p>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </section>
  );
}
